/**
 * COPY STRATEGY — CalculatorPage (Tax Calculator)
 *
 * Awareness:      Solution-Aware → Product-Aware (Stage 3→4/5)
 *                 Reader knows they can save with actual expenses but wants to
 *                 see their personal numbers. Interactive tool converts curiosity
 *                 into conviction — then into Finhudas signup.
 *
 * Sophistication: Stage 3 — Mechanism
 *                 The calculator IS the mechanism. Reader inputs their own data
 *                 and sees personalized GPM/VSD/PSD savings. More persuasive than
 *                 any article because the numbers are theirs, not hypothetical.
 *
 * Avatar:         Any driver considering actual expenses (mid-funnel).
 *                 Arrived from ExpenseNormPage, BlogIndexPage, or ads.
 *                 Earning €1000–4000/month. Wants proof before committing.
 *
 * Hook:           Personalization — "Sužinok per 30 sek. kiek sutaupytum"
 * Structure:      Input sliders → Instant results → Savings highlight → CTA
 * Proof:          Real-time calculation using actual Lithuanian tax formulas
 * Funnel:         Highest-conversion page — personal proof → Finhudas CTA
 */
import { useState, useMemo, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { PiGasPump, PiReceipt, PiCar, PiPercent, PiCaretDown } from 'react-icons/pi'
import mixpanel from 'mixpanel-browser'

const track = (event: string, properties?: Record<string, unknown>) => {
  try { mixpanel.track(event, properties) } catch { /* mixpanel not initialized */ }
}

import HeaderBack from '@/components/dashboard/Header/HeaderBack'
import MotionText from '@/components/common/Motion/MotionText'
import MotionDiv from '@/components/common/Motion/MotionDiv'
import ButtonLink from '@components/common/Button/ButtonLink'
import FinhudasCTA from '@/components/common/Blog/FinhudasCTA'
import RelatedArticles from '@/components/common/Blog/RelatedArticles'
import SliderInput from '@/components/common/Input/SliderInput'
import { usePageSetup } from '@/hooks/usePageSetup'
import { calculateTaxes } from '@/services/taxes/lithuania'

const COMMISSION_RATE = 0.25
const INVERSE_VAT_RATE = 0.21
const MEDIAN_TRIP_VALUE = 5.60
const CURRENT_YEAR = 2026

const fmtEur = (n: number) => new Intl.NumberFormat('lt-LT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n)

const AnimatedNumber = ({ value }: { value: number }) => {
  const [display, setDisplay] = useState(value)
  const ref = useRef<number | null>(null)

  useEffect(() => {
    const start = display
    const diff = value - start
    const duration = 400
    const startTime = performance.now()

    if (ref.current) cancelAnimationFrame(ref.current)

    const animate = (time: number) => {
      const elapsed = time - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setDisplay(Math.round(start + diff * eased))
      if (progress < 1) ref.current = requestAnimationFrame(animate)
    }

    ref.current = requestAnimationFrame(animate)
    return () => { if (ref.current) cancelAnimationFrame(ref.current) }
  }, [value])

  return <>{fmtEur(display)}</>
}

const CalculatorPage = () => {
  const { t } = useTranslation()
  usePageSetup(t('calculator.pageTitle'), { scroll: true })

  const [monthlyNet, setMonthlyNet] = useState(1700)
  const [expensePercent, setExpensePercent] = useState(60)
  const [carType, setCarType] = useState<'own' | 'rented'>('own')
  const [inputMode, setInputMode] = useState<'percent' | 'manual'>('percent')
  const [monthlyFuel, setMonthlyFuel] = useState(250)
  const [showOther, setShowOther] = useState(false)
  const [carRental, setCarRental] = useState(500)
  const [insurance, setInsurance] = useState(50)
  const [repair, setRepair] = useState(50)
  const [phone, setPhone] = useState(15)
  const [depreciation, setDepreciation] = useState(100)

  const hasTrackedResult = useRef(false)

  const results = useMemo(() => {
    const monthlyGross = monthlyNet / (1 - COMMISSION_RATE)
    const annualGross = monthlyGross * 12

    let totalActualExpenses: number

    if (inputMode === 'percent') {
      totalActualExpenses = annualGross * expensePercent / 100
    } else {
      const annualCommission = monthlyGross * COMMISSION_RATE * 12
      const annualInverseVat = annualCommission * INVERSE_VAT_RATE
      const annualFuel = monthlyFuel * 12
      const annualOther = carType === 'own'
        ? (insurance + repair + phone + depreciation) * 12
        : (carRental + phone) * 12
      totalActualExpenses = annualCommission + annualInverseVat + annualFuel + annualOther
    }

    const expenses30 = annualGross * 0.30
    const tax30 = calculateTaxes(annualGross, expenses30, CURRENT_YEAR, { method: 'fixed_30', monthsActive: 12 })
    const taxActual = calculateTaxes(annualGross, totalActualExpenses, CURRENT_YEAR, { method: 'actual', monthsActive: 12 })

    const annualSavings = Math.max(0, tax30.totalTax - taxActual.totalTax)
    const expenseRatio = annualGross > 0 ? Math.round(totalActualExpenses / annualGross * 100) : 0

    const monthlyCommission = monthlyGross * COMMISSION_RATE
    const monthlyInverseVat = monthlyCommission * INVERSE_VAT_RATE
    const monthlyTotal = monthlyGross * expensePercent / 100
    const monthlyDiscretionary = Math.max(0, monthlyTotal - monthlyCommission - monthlyInverseVat)

    const breakdown = carType === 'own' ? [
      { label: t('calculator.breakdown.commission'), value: monthlyCommission },
      { label: t('calculator.breakdown.reverseVat'), value: monthlyInverseVat },
      { label: t('calculator.breakdown.fuel'), value: monthlyDiscretionary * 0.55 },
      { label: t('calculator.breakdown.depreciation'), value: monthlyDiscretionary * 0.20 },
      { label: t('calculator.breakdown.insurance'), value: monthlyDiscretionary * 0.12 },
      { label: t('calculator.breakdown.repair'), value: monthlyDiscretionary * 0.08 },
      { label: t('calculator.breakdown.phone'), value: monthlyDiscretionary * 0.05 },
    ] : [
      { label: t('calculator.breakdown.commission'), value: monthlyCommission },
      { label: t('calculator.breakdown.reverseVat'), value: monthlyInverseVat },
      { label: t('calculator.breakdown.carRental'), value: monthlyDiscretionary * 0.55 },
      { label: t('calculator.breakdown.fuel'), value: monthlyDiscretionary * 0.35 },
      { label: t('calculator.breakdown.phone'), value: monthlyDiscretionary * 0.10 },
    ]

    return {
      annualGross,
      expenses30,
      totalActualExpenses,
      tax30,
      taxActual,
      annualSavings,
      monthlySavings: annualSavings / 12,
      tripsPerYear: Math.round(annualSavings / MEDIAN_TRIP_VALUE),
      tripsPerMonth: Math.round(annualSavings / MEDIAN_TRIP_VALUE / 12),
      expenseRatio,
      breakdown,
    }
  }, [monthlyNet, monthlyFuel, insurance, repair, phone, depreciation, carType, carRental, inputMode, expensePercent, t])

  useEffect(() => {
    if (results.annualSavings > 0 && !hasTrackedResult.current) {
      hasTrackedResult.current = true
      track("calculator_result_shown", {
        monthly_income: monthlyNet,
        annual_gross: Math.round(results.annualGross),
        expense_ratio: results.expenseRatio,
        annual_savings: Math.round(results.annualSavings),
        method_winner: results.annualSavings > 0 ? "actual" : "fixed_30",
      })
    }
  }, [results.annualSavings])

  const fmt = (n: number) => fmtEur(Math.round(n))

  return (
    <>
      <HeaderBack to="/blog" />
      <div className="min-h-full bg-white flex flex-col">

        {/* Section 1: COMPACT HOOK */}
        <div className="px-5">
          <MotionText as="p" className="text-zinc-500 text-sm mb-2">
            {t('calculator.hook.cta')}
          </MotionText>
          <MotionText
            as="h1"
            className="text-base-black text-2xl font-semibold leading-tight mb-1"
          >
            {t('calculator.hook.title')}
          </MotionText>
        </div>

        {/* Section 2: CALCULATOR INPUTS */}
        <div className="px-5 py-4">
          <SliderInput
            label={t('calculator.inputs.monthlyIncome')}
            icon={<PiCar className="w-4 h-4 text-zinc-500" />}
            value={monthlyNet}
            min={500}
            max={5000}
            step={50}
            onChange={setMonthlyNet}
            note={t('calculator.inputs.monthlyIncomeNote')}
          />

          {inputMode === 'percent' ? (
            <>
              <SliderInput
                label={t('calculator.inputs.expensePercent')}
                icon={<PiPercent className="w-4 h-4 text-zinc-500" />}
                value={expensePercent}
                min={31}
                max={75}
                step={1}
                suffix="%"
                onChange={setExpensePercent}
                note={t('calculator.inputs.expensePercentNote')}
              />

              <motion.button
                className="text-sm text-blue-600 font-medium py-2"
                onClick={() => {
                  setInputMode('manual')
                  track("calculator_switched_to_manual")
                }}
                whileTap={{ scale: 0.98 }}
              >
                {t('calculator.inputs.switchToManual')}
              </motion.button>
            </>
          ) : (
            <>
              <MotionDiv className="mb-4">
                <label className="text-sm text-zinc-700 mb-2 block">{t('calculator.inputs.car')}</label>
                <div className="flex bg-zinc-100 rounded-xl p-1">
                  <button
                    className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${carType === 'own' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500'
                      }`}
                    onClick={() => setCarType('own')}
                  >
                    {t('calculator.inputs.carOwn')}
                  </button>
                  <button
                    className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${carType === 'rented' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500'
                      }`}
                    onClick={() => setCarType('rented')}
                  >
                    {t('calculator.inputs.carRented')}
                  </button>
                </div>
              </MotionDiv>

              <SliderInput
                label={t('calculator.inputs.fuel')}
                icon={<PiGasPump className="w-4 h-4 text-zinc-500" />}
                value={monthlyFuel}
                min={0}
                max={800}
                step={10}
                onChange={setMonthlyFuel}
              />

              {carType === 'rented' && (
                <SliderInput
                  label={t('calculator.inputs.carRental')}
                  icon={<PiReceipt className="w-4 h-4 text-zinc-500" />}
                  value={carRental}
                  min={100}
                  max={1000}
                  step={10}
                  onChange={setCarRental}
                  note={t('calculator.inputs.carRentalNote')}
                />
              )}

              <motion.div
                className="flex items-center gap-2 py-2 cursor-pointer"
                onClick={() => {
                  setShowOther(!showOther)
                  if (!showOther) track("calculator_expanded_other_expenses")
                }}
                whileTap={{ scale: 0.98 }}
              >
                <MotionText as="span" className="text-sm text-blue-600 font-medium">
                  {t('calculator.inputs.otherExpenses')}
                </MotionText>
                <motion.div animate={{ rotate: showOther ? 180 : 0 }} transition={{ duration: 0.2 }}>
                  <PiCaretDown className="w-4 h-4 text-blue-600" />
                </motion.div>
              </motion.div>

              {showOther && (
                <MotionDiv className="mt-2">
                  {carType === 'own' ? (
                    <>
                      <SliderInput
                        label={t('calculator.inputs.insurance')}
                        value={insurance}
                        min={0}
                        max={200}
                        step={5}
                        onChange={setInsurance}
                      />
                      <SliderInput
                        label={t('calculator.inputs.repair')}
                        value={repair}
                        min={0}
                        max={300}
                        step={5}
                        onChange={setRepair}
                      />
                      <SliderInput
                        label={t('calculator.inputs.phone')}
                        value={phone}
                        min={0}
                        max={50}
                        step={1}
                        onChange={setPhone}
                      />
                      <SliderInput
                        label={t('calculator.inputs.depreciation')}
                        value={depreciation}
                        min={0}
                        max={500}
                        step={10}
                        onChange={setDepreciation}
                      />
                    </>
                  ) : (
                    <SliderInput
                      label={t('calculator.inputs.phone')}
                      value={phone}
                      min={0}
                      max={50}
                      step={1}
                      onChange={setPhone}
                    />
                  )}
                </MotionDiv>
              )}

              <motion.button
                className="text-sm text-blue-600 font-medium py-2"
                onClick={() => {
                  setInputMode('percent')
                  track("calculator_switched_to_percent")
                }}
                whileTap={{ scale: 0.98 }}
              >
                {t('calculator.inputs.switchToPercent')}
              </motion.button>
            </>
          )}

        </div>

        {/* Section 3: RESULTS */}
        <div className="px-5 py-6 bg-zinc-50">
          {/* Savings reveal */}
          {results.annualSavings > 0 && (
            <MotionDiv className="mb-4 p-5 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl">
              <MotionText as="p" className="text-blue-100 text-xs font-semibold mb-1">
                {t('calculator.results.savingsTitle')}
              </MotionText>
              <p className="text-white text-3xl font-semibold mb-1">
                <AnimatedNumber value={Math.round(results.annualSavings)} />
              </p>
              <MotionText as="p" className="text-blue-100 text-sm mb-3">
                {t('calculator.results.perMonth', { amount: fmt(results.monthlySavings) })}
              </MotionText>
              <div className="border-t border-blue-500 pt-3">
                <MotionText as="p" className="text-white text-sm">
                  {t('calculator.results.tripsPerYear', { count: results.tripsPerYear })}
                </MotionText>
                <MotionText as="p" className="text-blue-200 text-xs mt-1">
                  {t('calculator.results.tripsPerMonth', { count: results.tripsPerMonth })}
                </MotionText>
              </div>
            </MotionDiv>
          )}

          {results.annualSavings <= 0 && (
            <MotionDiv className="mb-4 p-4 bg-white border border-zinc-200 rounded-xl">
              <MotionText as="p" className="text-zinc-700 text-sm">
                {t('calculator.results.noSavings')}
              </MotionText>
            </MotionDiv>
          )}
          <MotionText as="p" className="text-zinc-600 text-sm leading-relaxed">
            {t('calculator.hook.subtitle')}
          </MotionText>

          <MotionText as="h2" className="text-base-black text-xl font-semibold my-4">
            {t('calculator.results.title')}
          </MotionText>

          <MotionDiv className="border border-zinc-200 rounded-xl overflow-hidden bg-white">
            <div className="grid grid-cols-3 bg-zinc-100 border-b border-zinc-200">
              <div className="p-2.5 pl-3" />
              <div className="p-2.5 text-center border-l border-zinc-200">
                <span className="text-red-700 text-xs font-semibold">❌ {t('calculator.results.column30')}</span>
              </div>
              <div className="p-2.5 text-center border-l border-zinc-200">
                <span className="text-green-700 text-xs font-semibold">✅ {t('calculator.results.columnActual')}</span>
              </div>
            </div>

            <div className="grid grid-cols-3 border-b border-zinc-100">
              <div className="p-2 pl-3"><span className="text-zinc-600 text-xs">{t('calculator.results.income')}</span></div>
              <div className="p-2 text-right pr-3 border-l border-zinc-100"><span className="text-zinc-700 text-xs font-mono">{fmt(results.annualGross)}</span></div>
              <div className="p-2 text-right pr-3 border-l border-zinc-100"><span className="text-zinc-700 text-xs font-mono">{fmt(results.annualGross)}</span></div>
            </div>

            <div className="grid grid-cols-3 border-b border-zinc-100">
              <div className="p-2 pl-3"><span className="text-zinc-600 text-xs">{t('calculator.results.expenses')}</span></div>
              <div className="p-2 text-right pr-3 border-l border-zinc-100"><span className="text-zinc-700 text-xs font-mono">{fmt(results.expenses30)}</span></div>
              <div className="p-2 text-right pr-3 border-l border-zinc-100"><span className="text-zinc-700 text-xs font-mono">{fmt(results.totalActualExpenses)}</span></div>
            </div>

            <div className="grid grid-cols-3 border-b border-zinc-200 bg-zinc-50">
              <div className="p-2 pl-3"><span className="text-zinc-700 text-xs font-semibold">{t('calculator.results.gpmBase')}</span></div>
              <div className="p-2 text-right pr-3 border-l border-zinc-200"><span className="text-zinc-700 text-xs font-mono font-semibold">{fmt(results.annualGross - results.expenses30)}</span></div>
              <div className="p-2 text-right pr-3 border-l border-zinc-200"><span className="text-zinc-700 text-xs font-mono font-semibold">{fmt(results.annualGross - results.totalActualExpenses)}</span></div>
            </div>

            <div className="grid grid-cols-3 border-b border-zinc-100">
              <div className="p-2 pl-3"><span className="text-zinc-600 text-xs">GPM</span></div>
              <div className="p-2 text-right pr-3 border-l border-zinc-100"><span className="text-red-700 text-xs font-mono">{fmt(results.tax30.gpmAmount)}</span></div>
              <div className="p-2 text-right pr-3 border-l border-zinc-100"><span className="text-green-700 text-xs font-mono">{fmt(results.taxActual.gpmAmount)}</span></div>
            </div>

            <div className="grid grid-cols-3 border-b border-zinc-100">
              <div className="p-2 pl-3"><span className="text-zinc-600 text-xs">VSD</span></div>
              <div className="p-2 text-right pr-3 border-l border-zinc-100"><span className="text-red-700 text-xs font-mono">{fmt(results.tax30.vsdAmount)}</span></div>
              <div className="p-2 text-right pr-3 border-l border-zinc-100"><span className="text-green-700 text-xs font-mono">{fmt(results.taxActual.vsdAmount)}</span></div>
            </div>

            <div className="grid grid-cols-3 border-b border-zinc-200">
              <div className="p-2 pl-3"><span className="text-zinc-600 text-xs">PSD</span></div>
              <div className="p-2 text-right pr-3 border-l border-zinc-100"><span className="text-red-700 text-xs font-mono">{fmt(results.tax30.psdPaid + results.tax30.psdDifference)}</span></div>
              <div className="p-2 text-right pr-3 border-l border-zinc-100"><span className="text-green-700 text-xs font-mono">{fmt(results.taxActual.psdPaid + results.taxActual.psdDifference)}</span></div>
            </div>

            <div className="grid grid-cols-3 bg-zinc-100">
              <div className="p-2.5 pl-3"><span className="text-zinc-900 text-xs font-semibold">{t('calculator.results.totalTax')}</span></div>
              <div className="p-2.5 text-right pr-3 border-l border-zinc-200">
                <span className="text-red-900 text-xs font-mono font-semibold">{fmt(results.tax30.totalTax)}</span>
                <span className="text-red-500 text-[10px] ml-1">({results.tax30.taxPercentage.toFixed(1)}%)</span>
              </div>
              <div className="p-2.5 text-right pr-3 border-l border-zinc-200">
                <span className="text-green-900 text-xs font-mono font-semibold">{fmt(results.taxActual.totalTax)}</span>
                <span className="text-green-500 text-[10px] ml-1">({results.taxActual.taxPercentage.toFixed(1)}%)</span>
              </div>
            </div>
          </MotionDiv>

          {inputMode === 'percent' && (
            <>
              <MotionDiv className="mt-4 p-3 bg-white border border-zinc-200 rounded-xl">
                <p className="text-[10px] text-zinc-400 uppercase font-semibold mb-1.5">{t('calculator.breakdown.title')}</p>
                <div className="space-y-1">
                  {results.breakdown.map((item) => (
                    <div key={item.label} className="flex justify-between text-xs">
                      <span className="text-zinc-500">{item.label}</span>
                      <span className="text-zinc-700 font-mono">~{fmt(item.value)}</span>
                    </div>
                  ))}
                </div>
              </MotionDiv>

              <MotionDiv className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <MotionText as="p" className="text-amber-900 text-sm font-semibold">
                  {t('calculator.amber.title')}
                </MotionText>
                <MotionText as="p" className="text-amber-800 text-xs mt-0.5">
                  {t('calculator.amber.subtitle')}
                </MotionText>
              </MotionDiv>
            </>
          )}
        </div>

        {/* Section 4: WHY THIS WORKS */}
        <div className="px-5 py-6">
          <MotionText as="h2" className="text-base-black text-xl font-semibold mb-4">
            {t('calculator.whyItWorks.title')}
          </MotionText>

          <MotionDiv className="p-4 bg-zinc-50 rounded-xl mb-3">
            <MotionText as="p" className="text-zinc-700 text-sm leading-relaxed mb-3">
              {t('calculator.whyItWorks.intro')}
            </MotionText>
            <div className="space-y-3">
              <div className="flex gap-3">
                <span className="text-zinc-400 text-sm font-semibold">1.</span>
                <div>
                  <MotionText as="p" className="text-zinc-700 text-sm font-semibold">{t('calculator.whyItWorks.method1Title')}</MotionText>
                  <MotionText as="p" className="text-zinc-500 text-xs">{t('calculator.whyItWorks.method1Desc')}</MotionText>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="text-zinc-400 text-sm font-semibold">2.</span>
                <div>
                  <MotionText as="p" className="text-zinc-700 text-sm font-semibold">{t('calculator.whyItWorks.method2Title')}</MotionText>
                  <MotionText as="p" className="text-zinc-500 text-xs">{t('calculator.whyItWorks.method2Desc')}</MotionText>
                </div>
              </div>
            </div>
          </MotionDiv>

          <MotionDiv className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <MotionText as="p" className="text-blue-900 text-sm font-semibold mb-1">
              {t('calculator.whyItWorks.bonusTitle')}
            </MotionText>
            <MotionText as="p" className="text-blue-800 text-xs leading-relaxed">
              {t('calculator.whyItWorks.bonusDesc')}
            </MotionText>
          </MotionDiv>

          <MotionText as="p" className="text-zinc-400 text-xs mt-2">
            {t('calculator.whyItWorks.legalBasis')}
          </MotionText>
        </div>

        {/* Section 5: WHAT YOU NEED + CTA */}
        <div className="px-5 py-6 bg-zinc-50">
          <MotionText as="h2" className="text-base-black text-xl font-semibold mb-4">
            {t('calculator.whatToDo.title')}
          </MotionText>

          <MotionDiv className="space-y-3 mb-5">
            <MotionDiv className="flex gap-3 items-start">
              <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-semibold">1</span>
              <div>
                <MotionText as="p" className="text-zinc-700 text-sm font-semibold">{t('calculator.whatToDo.step1')}</MotionText>
                <MotionText as="p" className="text-zinc-500 text-xs">{t('calculator.whatToDo.step1Desc')}</MotionText>
              </div>
            </MotionDiv>
            <MotionDiv className="flex gap-3 items-start">
              <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-semibold">2</span>
              <div>
                <MotionText as="p" className="text-zinc-700 text-sm font-semibold">{t('calculator.whatToDo.step2')}</MotionText>
                <MotionText as="p" className="text-zinc-500 text-xs">{t('calculator.whatToDo.step2Desc')}</MotionText>
              </div>
            </MotionDiv>
            <MotionDiv className="flex gap-3 items-start">
              <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-semibold">3</span>
              <div>
                <MotionText as="p" className="text-zinc-700 text-sm font-semibold">{t('calculator.whatToDo.step3')}</MotionText>
                <MotionText as="p" className="text-zinc-500 text-xs">{t('calculator.whatToDo.step3Desc')}</MotionText>
              </div>
            </MotionDiv>
          </MotionDiv>

          <FinhudasCTA />
        </div>

        {/* Section 6: RELATED + BACK */}
        <div className="px-5 py-6">
          <RelatedArticles articles={[
            { slug: 'individualios-veiklos-islaidos-30-procentu-arba-faktines', title: '30% norma ar faktinės išlaidos 2026' },
            { slug: '60-procentu-islaidos-strategija', title: '60% faktinės išlaidos: kaip mokėti tik 5% GPM' },
            { slug: 'kuro-apskaita-3-metodai-islaidos', title: 'Kuro išlaidos: 3 metodai ir dokumentų reikalavimai' },
            { slug: 'pavezejo-mokesciai-gpm-psd-vsd', title: 'Mokesčiai pavežėjui 2026: GPM, PSD, VSD ir PVM' },
          ]} />

          <motion.div
            className="mt-6 pb-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <ButtonLink variant="secondary" to="/blog">
              {t('calculator.backToArticles')}
            </ButtonLink>
          </motion.div>
        </div>
      </div>
    </>
  )
}

export default CalculatorPage
